import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../api/initSupabase';
import { FontText, PrimaryButton, SEOHead } from '../components/common';
import { logErrorsWithMessage } from '../utils/errors';
import { localAnalytics } from '../utils/analytics';
import { Link } from 'react-router-dom';

export const UnsubscribeEmail: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    localAnalytics().logEvent('UnsubscribeEmailPageVisited', {
      screen: 'UnsubscribeEmail',
      action: 'PageVisited',
    });

    const urlParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlParams.get('token');
    const emailFromUrl = urlParams.get('email');

    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError(t('invalid_unsubscribe_link'));
    }

    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [location.search, t]);

  const handleShowConfirmation = () => {
    localAnalytics().logEvent('UnsubscribeEmailConfirmationShown', {
      screen: 'UnsubscribeEmail',
      action: 'ConfirmationShown',
    });
    setShowConfirmation(true);
  };

  const handleCancel = () => {
    localAnalytics().logEvent('UnsubscribeEmailCancelled', {
      screen: 'UnsubscribeEmail',
      action: 'Cancelled',
    });
    setShowConfirmation(false);
  };

  const handleUnsubscribe = async () => {
    if (!token) {
      setError(t('invalid_unsubscribe_token'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      localAnalytics().logEvent('UnsubscribeEmailStarted', {
        screen: 'UnsubscribeEmail',
        action: 'UnsubscribeStarted',
      });

      const { data, error } = await supabase.functions.invoke(
        `unsubscribe-email?token=${token}`,
        { method: 'POST' }
      );

      if (error) {
        throw error;
      }

      if (data && data.success) {
        setUnsubscribed(true);
        setShowConfirmation(false);

        localAnalytics().logEvent('UnsubscribeEmailSuccess', {
          screen: 'UnsubscribeEmail',
          action: 'UnsubscribeSuccess',
        });
      } else {
        throw new Error(data?.message || t('unsubscribe_failed'));
      }
    } catch (error) {
      logErrorsWithMessage(error, t('unsubscribe_failed'));
      setError(t('unsubscribe_failed'));

      localAnalytics().logEvent('UnsubscribeEmailFailed', {
        screen: 'UnsubscribeEmail',
        action: 'UnsubscribeFailed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    content: {
      backgroundColor: theme.colors.surface,
      borderRadius: 20,
      padding: 40,
      maxWidth: 500,
      width: '100%',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    logo: {
      color: theme.colors.accent,
      marginBottom: 30,
    },
    icon: {
      fontSize: 48,
      marginBottom: 20,
    },
    title: {
      color: theme.colors.text,
      marginBottom: 20,
    },
    description: {
      color: theme.colors.textSecondary,
      marginBottom: 30,
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: 15,
      marginTop: 20,
    },
    footer: {
      marginTop: 30,
      paddingTop: 20,
      borderTop: `1px solid ${theme.colors.overlay}`,
      color: theme.colors.textSecondary,
    },
    footerLink: {
      color: theme.colors.accent,
      textDecoration: 'none',
    },
    errorMessage: {
      backgroundColor: 'red',
      color: theme.colors.white,
      padding: 15,
      borderRadius: 8,
      marginBottom: 20,
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
    },
    spinner: {
      width: 20,
      height: 20,
      border: `2px solid ${theme.colors.overlay}`,
      borderTop: `2px solid ${theme.colors.accent}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
  };

  if (unsubscribed) {
    return (
      <>
        <SEOHead
          title={t('unsubscribe_success_title')}
          description={t('unsubscribe_success_description')}
          canonical="/unsubscribe-email"
        />
        <div style={styles.container}>
          <div style={styles.content}>
            <FontText h1 style={styles.logo}>
              {t('app_name')}
            </FontText>
            <div style={styles.icon}>✅</div>
            <FontText h2 style={styles.title}>
              {t('unsubscribe_success_title')}
            </FontText>
            <FontText style={styles.description}>
              {t('unsubscribe_success_message')}
            </FontText>

            {email && (
              <FontText style={styles.description}>
                {t('unsubscribe_email_confirmed', { email })}
              </FontText>
            )}

            <FontText style={styles.description}>
              {t('unsubscribe_app_access_message')}
            </FontText>
            <div style={styles.footer}>
              <Link to="/" style={styles.footerLink}>
                <FontText small>{t('return_to_nanoreads')}</FontText>
              </Link>
              <FontText small> | </FontText>
              <a href="mailto:support@nanoreads.com" style={styles.footerLink}>
                <FontText small>{t('contact_support')}</FontText>
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={t('unsubscribe_email_title')}
        description={t('unsubscribe_email_description')}
        canonical="/unsubscribe-email"
      />
      <div style={styles.container}>
        <div style={styles.content}>
          <FontText h1 style={styles.logo}>
            {t('app_name')}
          </FontText>

          {error && (
            <div style={styles.errorMessage}>
              <FontText small>{error}</FontText>
            </div>
          )}

          {!showConfirmation ? (
            <>
              <div style={styles.icon}>📧</div>
              <FontText h2 style={styles.title}>
                {t('unsubscribe_email_title')}
              </FontText>

              {email && (
                <FontText style={styles.description}>
                  {t('unsubscribe_email_address', { email })}
                </FontText>
              )}

              <FontText style={styles.description}>
                {t('unsubscribe_email_description')}
              </FontText>

              <FontText style={styles.description}>
                {t('unsubscribe_email_note')}
              </FontText>

              <PrimaryButton
                onClick={handleShowConfirmation}
                disabled={!token || loading}
                style={{ marginBottom: 20 }}
              >
                {t('unsubscribe_from_emails')}
              </PrimaryButton>
            </>
          ) : (
            <>
              <div style={styles.icon}>⚠️</div>
              <FontText h2 style={styles.title}>
                {t('unsubscribe_confirmation_title')}
              </FontText>

              {email && (
                <FontText style={styles.description}>
                  {t('unsubscribe_confirmation_email', { email })}
                </FontText>
              )}

              <FontText style={styles.description}>
                {t('unsubscribe_confirmation_message')}
              </FontText>

              <FontText style={styles.description}>
                {t('unsubscribe_confirmation_note')}
              </FontText>

              <div style={styles.buttonContainer}>
                <PrimaryButton
                  onClick={handleUnsubscribe}
                  loading={loading}
                  disabled={loading}
                  style={{ backgroundColor: 'red' }}
                >
                  {loading ? (
                    <div style={styles.loading}>
                      <div style={styles.spinner}></div>
                      <FontText small>{t('processing')}</FontText>
                    </div>
                  ) : (
                    t('yes_unsubscribe')
                  )}
                </PrimaryButton>
                <PrimaryButton
                  onClick={handleCancel}
                  disabled={loading}
                  style={{
                    backgroundColor: theme.colors.overlay,
                    color: theme.colors.text,
                  }}
                >
                  {t('no_keep_emails')}
                </PrimaryButton>
              </div>
            </>
          )}

          <div style={styles.footer}>
            <FontText small>
              {t('need_help')}{' '}
              <a href="mailto:support@nanoreads.com" style={styles.footerLink}>
                {t('contact_support')}
              </a>{' '}
              |{' '}
              <Link to="/" style={styles.footerLink}>
                {t('return_to_nanoreads')}
              </Link>
            </FontText>
          </div>
        </div>
      </div>
    </>
  );
};
